import { get, post } from '../../../infra/rest';
import requestConfig from '../../../shared/config/request';
import { TAB_ID } from '../../../shared/utils/tab-id';
import { urlCreator } from '../../../shared/utils/url-helpers';
import { fetchWithBroadcast } from '../../../shared/utils/fetch-with-broadcast';
import {
  AnswerIncommingCallApiType,
  CallDirection,
  CallFromUpdatesApi,
  CreateOutgoingCallApiRequest,
  CreateOutgoingCallApiResponse,
  ReconnectSDPResponse,
  SdpPayloadType,
} from '../typings';

export const answerIncommingCallApi = async ({
  callId,
  sdp,
  sdpType,
  reconnect,
}: AnswerIncommingCallApiType): Promise<unknown> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/accept`,
  });

  return post<AnswerIncommingCallApiType>(
    url,
    {
      body: JSON.stringify({
        callId,
        sdp,
        sdpType,
        reconnect,
        deviceId: TAB_ID,
      }),
    },
    true,
    false,
    true
  );
};

export const declineCallApi = async ({ callId }: { callId: string }): Promise<unknown> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/decline`,
  });
  return post<{ callId: string; wabaNumber: string }>(
    url,
    {
      body: JSON.stringify({
        callId,
      }),
    },
    true,
    false,
    true
  );
};
export const getNewReconnectSDPApi = async ({
  callId,
  callOriginType,
  sdp,
  switchCall,
}: {
  callId: string;
  callOriginType: CallDirection;
  sdp?: string; // only for outgoing calls
  switchCall?: boolean;
}): Promise<ReconnectSDPResponse> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/reconnect`,
  });
  return post<ReconnectSDPResponse>(
    url,
    {
      body: JSON.stringify({
        callId,
        callOriginType,
        sdp,
        deviceId: TAB_ID,
        switchCall,
      }),
    },
    true,
    false,
    true
  );
};

// Keep-alive ping API to prevent resource leaks during active calls
export const keepAlivePingApi = async ({ callId }: { callId: string }): Promise<unknown> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/ping`,
    query: {
      callId,
    },
  });

  return get<unknown>(url, undefined, true, false, true);
};

export const getCallsUpdateApi = async (): Promise<{
  activeCalls: CallFromUpdatesApi[];
}> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/updates`,
  });
  const channelName = 'api-sync:call-updates';
  const cacheKey = 'cache:call-updates';
  return fetchWithBroadcast(
    channelName,
    cacheKey,
    () =>
      get<{
        activeCalls: CallFromUpdatesApi[];
      }>(url, {}, true, false, true),
    { ttlMs: 5000, waitTimeoutMs: 1500 }
  );
};

export const createOutgoingCallApi = async (
  body: CreateOutgoingCallApiRequest
): Promise<CreateOutgoingCallApiResponse> => {
  const url = urlCreator({
    baseUrl: `${requestConfig.aiBotBaseUrl}/v1/call/create`,
  });
  return post<CreateOutgoingCallApiResponse>(
    url,
    {
      body: JSON.stringify({
        ...body,
        deviceId: TAB_ID,
      }),
    },
    true,
    false,
    true
  );
};
